import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableColumn } from '../data-table/data-table.component';
import { DataListComponent } from '../data-list/data-list.component';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';

export interface BoardGroup { value: string; label: string; }

@Component({
  selector: 'app-data-board',
  imports: [CommonModule, DataListComponent, DragDropModule],
  template: `<div class="data-board" [class.data-board--draggable]="enableDragDrop" [style.--board-columns]="groups.length || 1" cdkDropListGroup>
    @for (group of groups; track group.value) {
      <section class="data-board__column" [attr.aria-label]="group.label" cdkDropList [cdkDropListData]="group.value" [cdkDropListDisabled]="!enableDragDrop" [cdkDropListSortingDisabled]="true" (cdkDropListDropped)="drop($event, group.value)">
        <h3>{{ group.label }} <small>{{ rows(group.value).length }}</small></h3>
        @for (row of rows(group.value); track row) {
          <div class="data-board__card" cdkDrag [cdkDragData]="row" [cdkDragDisabled]="!enableDragDrop">
            <app-data-list [data]="[row]" [columns]="columns" [cellTemplate]="cellTemplate" [itemTemplate]="cardTemplate" (rowClick)="rowClick.emit($event)" />
          </div>
        }
      </section>
    }
  </div>`,
  styles: [`:host { display: block; height: 100%; min-height: 0; } .data-board { display: grid; grid-template-columns: repeat(var(--board-columns), minmax(0, 1fr)); gap: 12px; padding: 12px; align-items: stretch; box-sizing: border-box; height: 100%; min-height: 0; }
    .data-board__column { min-width: 0; min-height: 120px; height: 100%; overflow-y: auto; box-sizing: border-box; padding: 10px; border-radius: 6px; background: var(--mat-sys-surface-container, #f5f7fa); transition: background-color 150ms ease; }
    .data-board--draggable .data-board__column.cdk-drop-list-receiving { background: var(--mat-sys-primary-container, #dbeafe); }
    .data-board--draggable .data-board__card { cursor: grab; }
    .data-board--draggable .data-board__card.cdk-drag-dragging { cursor: grabbing; opacity: .45; }
    .cdk-drag-preview { box-sizing: border-box; border-radius: 6px; box-shadow: 0 8px 20px #0003; }
    .cdk-drag-placeholder { opacity: .25; }
    h3 { position: sticky; top: -10px; z-index: 1; display: flex; align-items: center; justify-content: space-between; gap: 8px; font-size: 13px; margin: -10px -10px 12px; padding: 10px; background: var(--mat-sys-surface-container, #f5f7fa); }
    @media (max-width: 1100px) { .data-board { grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr)); } }
    small { font-weight: normal; } .data-board__card { background: var(--mat-sys-surface, #fff); border: 1px solid #e0e0e0; border-radius: 6px; margin-bottom: 10px; overflow-wrap: anywhere; }`],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataBoardComponent<T> {
  @Input() data: T[] | null | undefined = [];
  @Input() columns: TableColumn[] = [];
  @Input() groups: BoardGroup[] = [];
  @Input() groupBy = '';
  @Input() cellTemplate?: TemplateRef<any>;
  @Input() cardTemplate?: TemplateRef<any>;
  @Input() enableDragDrop = false;
  @Output() rowClick = new EventEmitter<T>();
  @Output() groupChange = new EventEmitter<{ row: T; group: string }>();
  rows(group: string): T[] { return (this.data ?? []).filter(row => String((row as Record<string, unknown>)[this.groupBy] ?? '') === group); }
  drop(event: CdkDragDrop<string, string, T>, group: string): void {
    if (!this.enableDragDrop || !event.isPointerOverContainer) return;
    const currentGroup = String((event.item.data as Record<string, unknown>)[this.groupBy] ?? '');
    if (currentGroup !== group) this.groupChange.emit({ row: event.item.data, group });
  }
}
